import { strFromU8, unzipSync } from 'fflate';

/**
 * Read the parts of a .docx that the report preview paints:
 * margins, the default header, the default footer, and body paragraphs.
 * Images in those parts are kept as bytes. Nothing here is stored.
 */

export type DocxAlign = 'left' | 'center' | 'right';

export type DocxRun =
  | { kind: 'text'; text: string; bold: boolean; italic: boolean; sizePt: number }
  | { kind: 'tab' }
  | { kind: 'break' }
  | { kind: 'page' }
  | {
      kind: 'field';
      instruction: 'PAGE' | 'NUMPAGES';
      bold: boolean;
      italic: boolean;
      sizePt: number;
    }
  | { kind: 'tocPage'; bold: boolean; italic: boolean; sizePt: number; title?: string }
  | { kind: 'image'; bytes: Uint8Array; widthPt: number; heightPt: number };

export type DocxTab = { val: string; posPt: number };

export type DocxLineRule = 'auto' | 'exact' | 'atLeast';

export type DocxParagraph = {
  align: DocxAlign;
  beforePt: number;
  afterPt: number;
  lineTwips: number | null;
  lineRule: DocxLineRule | null;
  /** Font size used when the paragraph has no text, such as a blank line. */
  fontPt: number;
  tabs: DocxTab[];
  runs: DocxRun[];
};

export type DocxMargins = {
  topPt: number;
  rightPt: number;
  bottomPt: number;
  leftPt: number;
  headerPt: number;
  footerPt: number;
};

export type DocxLayout = {
  margin: DocxMargins;
  header: DocxParagraph[];
  footer: DocxParagraph[];
  body: DocxParagraph[];
};

const PARAGRAPH_RE = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g;
const RUN_RE = /<w:r\b[^>]*\/>|<w:r\b[^>]*>[\s\S]*?<\/w:r>/g;
const TOKEN_RE =
  /<w:fldChar\b[^>]*\/>|<w:instrText\b[^>]*>[\s\S]*?<\/w:instrText>|<w:tab\b[^>]*\/>|<w:br\b[^>]*\/>|<w:cr\b[^>]*\/>|<w:t\b[^>]*>[\s\S]*?<\/w:t>|<w:drawing\b[\s\S]*?<\/w:drawing>/g;

const DEFAULT_MARGIN: DocxMargins = {
  topPt: 56,
  rightPt: 56,
  bottomPt: 56,
  leftPt: 56,
  headerPt: 36,
  footerPt: 36,
};

export function readDocxLayout(bytes: Uint8Array): DocxLayout {
  const files = unzipSync(bytes.slice());
  const documentXml = xml(files, 'word/document.xml');
  if (!documentXml) throw new Error('docx-type');
  const rels = xml(files, 'word/_rels/document.xml.rels') ?? '';
  const styles = parseStyles(xml(files, 'word/styles.xml'));
  const headerName = partName(documentXml, rels, 'header');
  const footerName = partName(documentXml, rels, 'footer');
  return {
    margin: marginsOf(documentXml),
    header: headerName ? paragraphs(xml(files, headerName) ?? '', files, headerName, styles) : [],
    footer: footerName ? paragraphs(xml(files, footerName) ?? '', files, footerName, styles) : [],
    body: paragraphs(documentXml, files, 'word/document.xml', styles),
  };
}

function xml(files: Record<string, Uint8Array>, name: string): string | null {
  const data = files[name] ?? files[name.replace(/\//g, '\\')];
  return data ? strFromU8(data) : null;
}

function partName(documentXml: string, rels: string, kind: 'header' | 'footer'): string | null {
  const tag = kind === 'header' ? 'headerReference' : 'footerReference';
  const refs = [...documentXml.matchAll(new RegExp(`<w:${tag}\\b[^>]*>`, 'g'))].map(
    (match) => match[0],
  );
  const chosen =
    refs.find((ref) => /w:type="default"/.test(ref)) ??
    refs.find((ref) => !/w:type="first"/.test(ref)) ??
    null;
  const id = chosen?.match(/\br:id="([^"]+)"/)?.[1];
  if (!id) return null;
  const rel = [...rels.matchAll(/<Relationship\b[^>]*>/g)]
    .map((match) => match[0])
    .find((item) => new RegExp(`\\bId="${id}"`).test(item));
  const target = rel?.match(/\bTarget="([^"]+)"/)?.[1];
  if (!target) return null;
  const clean = target.replace(/^\//, '').replace(/\\/g, '/');
  return clean.startsWith('word/') ? clean : `word/${clean}`;
}

function marginsOf(documentXml: string): DocxMargins {
  const tag = documentXml.match(/<w:pgMar\b[^>]*\/>/)?.[0];
  if (!tag) return DEFAULT_MARGIN;
  const pt = (name: string, fallback: number) => {
    const raw = tag.match(new RegExp(`w:${name}="(-?\\d+)"`))?.[1];
    if (!raw) return fallback;
    const value = Number(raw) / 20;
    return Number.isFinite(value) ? Math.min(Math.max(value, 18), 120) : fallback;
  };
  return {
    topPt: pt('top', DEFAULT_MARGIN.topPt),
    rightPt: pt('right', DEFAULT_MARGIN.rightPt),
    bottomPt: pt('bottom', DEFAULT_MARGIN.bottomPt),
    leftPt: pt('left', DEFAULT_MARGIN.leftPt),
    headerPt: pt('header', DEFAULT_MARGIN.headerPt),
    footerPt: pt('footer', DEFAULT_MARGIN.footerPt),
  };
}

function paragraphs(
  partXml: string,
  files: Record<string, Uint8Array>,
  partPath: string,
  styles: StyleSheet,
): DocxParagraph[] {
  const rels = xml(files, relsPath(partPath)) ?? '';
  return [...partXml.matchAll(PARAGRAPH_RE)].flatMap((match) => {
    const item = paragraph(match[0], files, rels, styles);
    return item ? [item] : [];
  });
}

function relsPath(part: string): string {
  const slash = part.lastIndexOf('/');
  return `${part.slice(0, slash)}/_rels/${part.slice(slash + 1)}.rels`;
}

function paragraph(
  xmlSource: string,
  files: Record<string, Uint8Array>,
  rels: string,
  styles: StyleSheet,
): DocxParagraph | null {
  const open = xmlSource.match(/^<w:p\b[^>]*>/)?.[0] ?? '';
  const pPr = xmlSource.match(/<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
  const align = alignOf(pPr);
  const spacing = spacingOf(pPr, xmlSource, styles);
  const tabs = tabsOf(pPr);
  const runs: DocxRun[] = [];
  let field: 'text' | 'instr' | 'result' = 'text';
  let instruction = '';
  let fieldPushed = false;
  for (const runXml of xmlSource.slice(open.length).matchAll(RUN_RE)) {
    const rPr = runXml[0].match(/<w:rPr>[\s\S]*?<\/w:rPr>/)?.[0] ?? '';
    const bold = /<w:b\b(?![^>]*w:val="(?:0|false|off)")/.test(rPr);
    const italic = /<w:i\b(?![^>]*w:val="(?:0|false|off)")/.test(rPr);
    const sizePt = sizeOf(rPr);
    for (const token of runXml[0].matchAll(TOKEN_RE)) {
      const tag = token[0];
      if (tag.startsWith('<w:fldChar')) {
        const type = tag.match(/w:fldCharType="([^"]+)"/)?.[1];
        if (type === 'begin') {
          field = 'instr';
          instruction = '';
          fieldPushed = false;
        } else if (type === 'separate') {
          field = 'result';
          pushField(runs, instruction, bold, italic, sizePt);
          fieldPushed = true;
        } else if (type === 'end') {
          if (!fieldPushed) pushField(runs, instruction, bold, italic, sizePt);
          field = 'text';
          instruction = '';
          fieldPushed = false;
        }
        continue;
      }
      if (tag.startsWith('<w:instrText')) {
        if (field === 'instr') instruction += decodeXml(innerText(tag));
        continue;
      }
      if (field === 'result' || field === 'instr') continue;
      if (tag.startsWith('<w:tab')) {
        runs.push({ kind: 'tab' });
        continue;
      }
      if (tag.startsWith('<w:br') || tag.startsWith('<w:cr')) {
        runs.push(
          tag.startsWith('<w:br') && /w:type="page"/.test(tag)
            ? { kind: 'page' }
            : { kind: 'break' },
        );
        continue;
      }
      if (tag.startsWith('<w:drawing')) {
        const image = imageRun(tag, files, rels);
        if (image) runs.push(image);
        continue;
      }
      if (tag.startsWith('<w:t')) {
        const text = decodeXml(innerText(tag));
        if (text) runs.push({ kind: 'text', text, bold, italic, sizePt });
      }
    }
  }
  // The last paragraph often exists only to hold section properties.
  // It is not a blank line between content.
  if (/<w:sectPr[\s>]/.test(pPr) && runs.length === 0) return null;
  return {
    align,
    beforePt: spacing.beforePt,
    afterPt: spacing.afterPt,
    lineTwips: spacing.lineTwips,
    lineRule: spacing.lineRule,
    fontPt: spacing.fontPt,
    tabs,
    runs,
  };
}

function pushField(
  runs: DocxRun[],
  instruction: string,
  bold: boolean,
  italic: boolean,
  sizePt: number,
) {
  const compact = instruction.replace(/\s+/g, ' ').trim();
  const normalized = compact.toUpperCase();
  if (normalized === 'TOC' || normalized.startsWith('TOC ')) {
    const title = /^toc$/i.test(compact) ? '' : compact.replace(/^toc\s+/i, '').trim();
    runs.push({ kind: 'tocPage', bold, italic, sizePt, title: title || undefined });
    return;
  }
  if (normalized.startsWith('NUMPAGES')) {
    runs.push({ kind: 'field', instruction: 'NUMPAGES', bold, italic, sizePt });
    return;
  }
  if (normalized.startsWith('PAGE')) {
    runs.push({ kind: 'field', instruction: 'PAGE', bold, italic, sizePt });
  }
}

function imageRun(
  drawing: string,
  files: Record<string, Uint8Array>,
  rels: string,
): DocxRun | null {
  const id = drawing.match(/\br:embed="([^"]+)"/)?.[1];
  if (!id) return null;
  const rel = [...rels.matchAll(/<Relationship\b[^>]*>/g)]
    .map((match) => match[0])
    .find((item) => new RegExp(`\\bId="${id}"`).test(item));
  const target = rel?.match(/\bTarget="([^"]+)"/)?.[1];
  if (!target) return null;
  const clean = target.replace(/^\//, '').replace(/\\/g, '/');
  const path = clean.startsWith('word/')
    ? clean
    : `word/${clean.replace(/^\.\.\//, '').replace(/^media\//, 'media/')}`;
  const bytes = files[path] ?? files[path.replace(/\//g, '\\')];
  if (!bytes || !isRaster(bytes)) return null;
  const cx = Number(drawing.match(/\bcx="(\d+)"/)?.[1] ?? 0);
  const cy = Number(drawing.match(/\bcy="(\d+)"/)?.[1] ?? 0);
  const widthPt = cx > 0 ? cx / 12700 : 120;
  const heightPt = cy > 0 ? cy / 12700 : 40;
  return { kind: 'image', bytes, widthPt, heightPt };
}

function isRaster(bytes: Uint8Array): boolean {
  if (bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50) return true;
  return bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8;
}

function alignOf(pPr: string): DocxAlign {
  const value = pPr.match(/<w:jc\b[^>]*w:val="([^"]+)"/)?.[1];
  if (value === 'center') return 'center';
  if (value === 'right' || value === 'end') return 'right';
  return 'left';
}

type SpacingSpec = {
  beforeTwips: number | null;
  afterTwips: number | null;
  lineTwips: number | null;
  lineRule: DocxLineRule | null;
};

type ParagraphStyle = {
  basedOn: string | null;
  spacing: SpacingSpec | null;
  fontHalfPoints: number | null;
};

type StyleSheet = {
  defaultId: string | null;
  defaults: SpacingSpec | null;
  defaultFontHalfPoints: number | null;
  byId: Map<string, ParagraphStyle>;
};

const EMPTY_SPACING: SpacingSpec = {
  beforeTwips: null,
  afterTwips: null,
  lineTwips: null,
  lineRule: null,
};

function spacingOf(
  pPr: string,
  paragraphXml: string,
  styles: StyleSheet,
): {
  beforePt: number;
  afterPt: number;
  lineTwips: number | null;
  lineRule: DocxLineRule | null;
  fontPt: number;
} {
  const explicit = pPr.match(/<w:pStyle\b[^>]*w:val="([^"]+)"/)?.[1] ?? null;
  // Word omits w:pStyle when the paragraph uses the document's default style.
  // Spacing set on that style still applies.
  const chain = styleChain(styles, explicit ?? styles.defaultId);
  let spec = styles.defaults ?? EMPTY_SPACING;
  for (const style of chain) {
    if (style.spacing) spec = overlaySpacing(spec, style.spacing);
  }
  const direct = spacingSpec(pPr);
  if (direct) spec = overlaySpacing(spec, direct);
  return {
    beforePt: spec.beforeTwips == null ? 0 : Math.max(0, spec.beforeTwips / 20),
    afterPt: spec.afterTwips == null ? 8 : Math.max(0, spec.afterTwips / 20),
    lineTwips: spec.lineTwips,
    lineRule: spec.lineTwips == null ? null : (spec.lineRule ?? 'auto'),
    fontPt: fontPtOf(pPr, paragraphXml, chain, styles),
  };
}

function overlaySpacing(base: SpacingSpec, over: SpacingSpec): SpacingSpec {
  return {
    beforeTwips: over.beforeTwips ?? base.beforeTwips,
    afterTwips: over.afterTwips ?? base.afterTwips,
    lineTwips: over.lineTwips ?? base.lineTwips,
    lineRule: over.lineRule ?? base.lineRule,
  };
}

function parseStyles(source: string | null): StyleSheet {
  const sheet: StyleSheet = {
    defaultId: null,
    defaults: null,
    defaultFontHalfPoints: null,
    byId: new Map(),
  };
  if (!source) return sheet;
  const paragraphDefaults = source.match(/<w:pPrDefault>[\s\S]*?<\/w:pPrDefault>/)?.[0] ?? '';
  const runDefaults = source.match(/<w:rPrDefault>[\s\S]*?<\/w:rPrDefault>/)?.[0] ?? '';
  sheet.defaults = spacingSpec(paragraphDefaults);
  sheet.defaultFontHalfPoints = fontHalf(runDefaults);
  for (const match of source.matchAll(/<w:style\b[^>]*>[\s\S]*?<\/w:style>/g)) {
    const block = match[0];
    const open = block.match(/^<w:style\b[^>]*>/)?.[0] ?? '';
    if (!/\bw:type="paragraph"/.test(open)) continue;
    const id = open.match(/\bw:styleId="([^"]+)"/)?.[1];
    if (!id) continue;
    const pPr = block.match(/<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
    sheet.byId.set(id, {
      basedOn: block.match(/<w:basedOn\b[^>]*w:val="([^"]+)"/)?.[1] ?? null,
      spacing: spacingSpec(pPr),
      fontHalfPoints: fontHalf(block),
    });
    if (/\bw:default="(?:1|true|on)"/.test(open)) sheet.defaultId = id;
  }
  return sheet;
}

function styleChain(styles: StyleSheet, styleId: string | null): ParagraphStyle[] {
  const out: ParagraphStyle[] = [];
  const seen = new Set<string>();
  let cursor = styleId;
  while (cursor && !seen.has(cursor.toLowerCase())) {
    seen.add(cursor.toLowerCase());
    const style = styleById(styles, cursor);
    if (!style) break;
    out.push(style);
    cursor = style.basedOn;
  }
  return out.reverse();
}

function styleById(styles: StyleSheet, id: string): ParagraphStyle | null {
  const direct = styles.byId.get(id);
  if (direct) return direct;
  const needle = id.toLowerCase();
  for (const [key, value] of styles.byId) {
    if (key.toLowerCase() === needle) return value;
  }
  return null;
}

function spacingSpec(container: string): SpacingSpec | null {
  if (!container) return null;
  const tag =
    container.match(/<w:spacing\b[^>]*\/>/)?.[0] ??
    container.match(/<w:spacing\b[^>]*>/)?.[0] ??
    '';
  if (!tag) return null;
  const num = (name: string) => {
    const raw = tag.match(new RegExp(`\\bw:${name}="(-?\\d+)"`))?.[1];
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  };
  const before = num('before');
  const after = num('after');
  const beforeLines = num('beforeLines');
  const afterLines = num('afterLines');
  const rule = tag.match(/\bw:lineRule="([^"]+)"/)?.[1] ?? null;
  return {
    beforeTwips: before ?? (beforeLines == null ? null : Math.round((beforeLines / 100) * 240)),
    afterTwips: after ?? (afterLines == null ? null : Math.round((afterLines / 100) * 240)),
    lineTwips: num('line'),
    lineRule: rule === 'exact' || rule === 'atLeast' || rule === 'auto' ? rule : null,
  };
}

function fontPtOf(
  pPr: string,
  paragraphXml: string,
  chain: ParagraphStyle[],
  styles: StyleSheet,
): number {
  const mark = fontHalf(pPr);
  if (mark) return clampFont(mark);
  const run = fontHalf(paragraphXml);
  if (run) return clampFont(run);
  for (let index = chain.length - 1; index >= 0; index -= 1) {
    const half = chain[index]?.fontHalfPoints;
    if (half) return clampFont(half);
  }
  if (styles.defaultFontHalfPoints) return clampFont(styles.defaultFontHalfPoints);
  return 11;
}

function fontHalf(container: string): number | null {
  const raw = container.match(/<w:sz\b[^>]*\bw:val="(\d+)"/)?.[1];
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function clampFont(halfPoints: number): number {
  return Math.min(Math.max(halfPoints / 2, 8), 28);
}

function tabsOf(pPr: string): DocxTab[] {
  return [...pPr.matchAll(/<w:tab\b[^>]*\/>/g)].map((match) => ({
    val: match[0].match(/w:val="([^"]+)"/)?.[1] ?? 'left',
    posPt: Number(match[0].match(/w:pos="(-?\d+)"/)?.[1] ?? 0) / 20,
  }));
}

function sizeOf(rPr: string): number {
  const half = Number(rPr.match(/<w:sz\b[^>]*w:val="(\d+)"/)?.[1] ?? 22);
  if (!Number.isFinite(half) || half <= 0) return 11;
  return Math.min(Math.max(half / 2, 8), 28);
}

function innerText(tag: string): string {
  return tag.replace(/^<[^>]+>/, '').replace(/<\/[^>]+>$/, '');
}

function decodeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, value: string) => String.fromCodePoint(Number(value)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, value: string) => String.fromCodePoint(parseInt(value, 16)))
    .replace(/&amp;/g, '&');
}
